import { useState } from "react";
import PropTypes from 'prop-types';
// @mui
import Stack from '@mui/material/Stack';
import CardHeader from '@mui/material/CardHeader';
import Typography from '@mui/material/Typography';
import {useTheme} from '@mui/material/styles';
import Card from '@mui/material/Card';
import LinearProgress from '@mui/material/LinearProgress';
import { Box } from '@mui/system';
import { CardContent, Collapse, IconButton, Tooltip } from '@mui/material';
// utils
import { fPercentEuro, fCurrencyEuro, fPercent } from '../../utils/format-number';
import Iconify from "../../components/iconify";

// ----------------------------------------------------------------------

export default function FinancialPercentOverview({ title, subheader, data, ...other }) {
  const theme = useTheme();
  const colors = theme.palette.simplai.array || [];
  const [open, setOpen] = useState({});

  const toggleOpen = (idx) => {
    setOpen((prev) => ({ ...prev, [idx]: !prev[idx] }));
  };

  return (
    <Card {...other}>
      {title && (<CardHeader title={title} subheader={subheader} />)}

      <Stack spacing={4} sx={{ px: 3, pt: 3, pb: 5 }}>
        {data.map((progress, idx) => (
          <Box key={progress.label} sx={{ mb: 2 }}>
            {/* Cabecera con botón expandir */}
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box sx={{ flexGrow: 1 }}>
                <ProgressItem
                  progress={progress}
                  colorIndex={colors.length ? idx % colors.length : undefined}
                  colors={colors}
                />
              </Box>

              {progress.sub && (
                <IconButton onClick={() => toggleOpen(idx)}>
                  {open[idx] ? <Iconify icon="ic:round-expand-less" width={30} height={30} color={theme.palette.text.secondary} /> : <Iconify icon="ic:round-expand-more" width={30} height={30} color={theme.palette.text.secondary} />}
                </IconButton>
              )}
            </Stack>

            {/* Subcategorías con Collapse */}
            {progress.sub && (
              <Collapse in={open[idx]} timeout="auto" unmountOnExit>
                <Card sx={{ mt: 1, ml: 4 }}>
                  <CardContent>
                    <Stack spacing={2}>
                      {progress.sub.map((subProgress, idx2) => (
                        <ProgressItem
                          key={subProgress.label}
                          progress={subProgress}
                          colorIndex={colors.length ? (idx2 + 1) % colors.length : undefined}
                          colors={colors}
                        />
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
              </Collapse>
            )}
          </Box>
        ))}
      </Stack>
    </Card>
  );
}

FinancialPercentOverview.propTypes = {
  data: PropTypes.array,
  subheader: PropTypes.string,
  title: PropTypes.string,
};

// ----------------------------------------------------------------------

function ProgressItem({ progress, colorIndex, colors }) {
  const theme = useTheme();
  

  return (
    <Stack spacing={1}>
      <Stack direction="row" alignItems="center">
        <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
          {progress.label}
        </Typography>
      </Stack>

      {progress.value.map((val, idx) => ( 
        <Stack key={idx} direction="row" alignItems="center" spacing={2}>
          <Typography variant="caption" color="text.secondary" minWidth={24} textAlign="left">
            {idx === 0 ? "1m" : "6m"}
          </Typography>
          <Tooltip title={idx === 0 ? "Últimos 30 días" : "5 meses anteriores"}>
            <Box sx={{ flexGrow: 1, maxWidth: val !== '0' ? fPercent(val) : "1%" }}>
              <LinearProgress
                variant="determinate"
                value={100}
                sx={{
                  height: 8,
                  bgcolor: '#e0e0e0',
                  borderRadius: 2,
                  '& .MuiLinearProgress-bar': {
                    backgroundColor:
                      idx !== 0 ? colors[colorIndex] : theme.palette.simplai.alter[colorIndex],
                  },
                }}
              />
            </Box>
          </Tooltip>

          <Typography variant="caption" color="text.primary" minWidth={50} textAlign="right">
            {fCurrencyEuro(progress.totalAmount[idx])}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}

ProgressItem.propTypes = {
  progress: PropTypes.object,
  colorIndex: PropTypes.number,
  colors: PropTypes.string,
};
